/**
 * Attribute Governance Store
 * 
 * Manages attribute governance templates and cycle-specific configurations.
 * Handles the value range definition/acceptance workflow and permission checks.
 * 
 * SUPABASE-ONLY PERSISTENCE:
 * - Primary: Supabase (single source of truth)
 * - No localStorage cache (eliminates stale data issues)
 */

import { create } from 'zustand';
import type { ActorRole } from '../../types/prioritization/roles';
import type { CapabilityId } from '../../types/prioritization/capabilityIds';
import {
  fetchAll,
  insertRecord,
  updateRecord,
  deleteRecord,
  isSupabaseAvailable,
  getSafeSupabaseClient,
  createInitialSyncState,
  generateUUID,
  type SyncState,
} from '../../lib/supabaseSync';
import {
  canDefineRange as checkCanDefineRange,
  canApproveRange as checkCanApproveRange,
  canProposeValue as checkCanProposeValue,
  canApproveValue as checkCanApproveValue,
  checkAttributePermission,
  type AttributeGovernanceConfig,
} from '../../lib/authorization/attributePermissions';
import { checkAuthorization, buildResourceContext } from '../../lib/authorization/enforce';
import { checkTrackWeights, type TrackWeights } from '../../lib/trackCriteriaWeights';
import {
  resolveWorkflowCriteriaGovernance,
  checkCanRemoveAttribute,
  checkCanProposeRange,
  getLockedRange,
} from '../../lib/workflowCriteriaGovernance';

// ============================================================================
// Types
// ============================================================================

/**
 * Collection method for gathering attribute values
 */
export type CollectionMethod = 
  | 'any_group_member'    // Any member of the designated group can provide the value
  | 'specific_person'     // A specific person is assigned to provide the value
  | 'tool_result'         // Value is gathered from tool usage results
  | 'facilitator_entry'   // Facilitator enters values during live session
  | 'participant_vote';   // Participants vote/score, results aggregated

/**
 * Range status for cycle attribute configurations
 */
export type RangeStatus = 'pending' | 'proposed' | 'accepted';

/**
 * Value range definition (e.g., Fibonacci, T-shirt sizes)
 */
export interface ValueRange {
  type: 'select' | 'number' | 'scale' | 'fibonacci' | 'custom';
  options?: (string | number)[];
  min?: number;
  max?: number;
  step?: number;
  labels?: Record<string, string>;
}

/**
 * Global attribute governance template
 */
export interface AttributeGovernanceTemplate {
  id: string;
  attributeKey: string;
  
  // Range definition roles
  rangeDefinerRoles: ActorRole[];
  rangeApproverRoles: ActorRole[];
  
  // Value provision roles
  valueProposerRoles: ActorRole[];
  valueApproverRoles: ActorRole[];
  
  // Collection method
  collectionMethod: CollectionMethod;
  specificPersonId?: string;
  
  // Evidence requirements
  requiresEvidence: boolean;
  evidenceTypes: string[];
  
  // Scope
  scopeKind: string;
  scopeId?: string;
  
  // Metadata
  isActive: boolean;
  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Cycle-level governance override (no track_id; applies to all tracks in the cycle unless overridden at track).
 * Stored in cycle_attribute_governance_overrides.
 */
export interface CycleAttributeGovernanceOverride {
  id: string;
  cycleId: string;
  attributeKey: string;
  rangeDefinerRoles?: ActorRole[];
  rangeApproverRoles?: ActorRole[];
  valueProposerRoles?: ActorRole[];
  valueApproverRoles?: ActorRole[];
  collectionMethod?: CollectionMethod;
  specificPersonId?: string;
  requiresEvidence?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Cycle-specific attribute configuration
 */
/**
 * A track's criteria: its Criteria & Scales rows. A cycle-bound track's rows carry
 * its cycle; a standalone track's carry none. The track page's Criteria tab and the
 * item page's Prioritization tab both read a track's criteria through this.
 */
export function criteriaRowsForTrack(
  configs: CycleAttributeConfig[],
  track: { id: string; cycleId?: string | null },
): CycleAttributeConfig[] {
  return configs.filter((c) => (track.cycleId ? c.cycleId === track.cycleId : !c.cycleId) && c.trackId === track.id);
}

export interface CycleAttributeConfig {
  id: string;
  /** Omitted (undefined/null) for standalone tracks not bound to a cycle. */
  cycleId?: string | null;
  /**
   * REQUIRED: Track this criteria config belongs to.
   * Per Track-Authoritative Cycle Spec: Criteria are track-scoped, not cycle-scoped.
   */
  trackId: string;
  attributeKey: string;

  // Overrides (null = use global template)
  rangeDefinerRoles?: ActorRole[];
  rangeApproverRoles?: ActorRole[];
  valueProposerRoles?: ActorRole[];
  valueApproverRoles?: ActorRole[];
  /** Where this criterion's values come from on this track (migration 1062); null or absent = not said. */
  collectionMethod?: CollectionMethod | null;
  specificPersonId?: string | null;
  requiresEvidence?: boolean;

  // Range status and value
  rangeStatus: RangeStatus;
  rangeValue?: ValueRange;
  rangeProposedBy?: string;
  rangeProposedAt?: Date;
  rangeAcceptedBy?: string;
  rangeAcceptedAt?: Date;

  /**
   * This criterion's share of the track's balance, a percentage 0–100 (migration
   * 1047); null or absent = none set. Saved only through saveTrackCriteriaWeights.
   */
  weight?: number | null;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Effective configuration (merged template + cycle overrides)
 */
export interface EffectiveAttributeConfig {
  attributeKey: string;
  cycleId?: string;
  
  rangeDefinerRoles: ActorRole[];
  rangeApproverRoles: ActorRole[];
  valueProposerRoles: ActorRole[];
  valueApproverRoles: ActorRole[];
  collectionMethod: CollectionMethod;
  specificPersonId?: string;
  requiresEvidence: boolean;
  evidenceTypes: string[];
  
  // Range info (from cycle config if available)
  rangeStatus?: RangeStatus;
  rangeValue?: ValueRange;
  
  // Source tracking
  isFromTemplate: boolean;
  templateId?: string;
  cycleConfigId?: string;
}

// ============================================================================
// Table Names
// ============================================================================

const TEMPLATES_TABLE = 'attribute_governance_templates';
const CYCLE_CONFIGS_TABLE = 'cycle_attribute_configs';
const CYCLE_OVERRIDES_TABLE = 'cycle_attribute_governance_overrides';

// ============================================================================
// Supabase to Store Mapping
// ============================================================================

function templateFromSupabase(row: any): AttributeGovernanceTemplate {
  return {
    id: row.id,
    attributeKey: row.attribute_key,
    rangeDefinerRoles: row.range_definer_roles || [],
    rangeApproverRoles: row.range_approver_roles || [],
    valueProposerRoles: row.value_proposer_roles || [],
    valueApproverRoles: row.value_approver_roles || [],
    collectionMethod: row.collection_method || 'any_group_member',
    specificPersonId: row.specific_person_id,
    requiresEvidence: row.requires_evidence || false,
    evidenceTypes: row.evidence_types || [],
    scopeKind: row.scope_kind || 'global',
    scopeId: row.scope_id,
    isActive: row.is_active ?? true,
    createdBy: row.created_by,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
}

function templateToSupabase(template: Partial<AttributeGovernanceTemplate>): Record<string, unknown> {
  const now = new Date().toISOString();
  return {
    id: template.id,
    attribute_key: template.attributeKey,
    range_definer_roles: template.rangeDefinerRoles,
    range_approver_roles: template.rangeApproverRoles,
    value_proposer_roles: template.valueProposerRoles,
    value_approver_roles: template.valueApproverRoles,
    collection_method: template.collectionMethod,
    specific_person_id: template.specificPersonId,
    requires_evidence: template.requiresEvidence,
    evidence_types: template.evidenceTypes,
    scope_kind: template.scopeKind,
    scope_id: template.scopeId,
    is_active: template.isActive,
    created_by: template.createdBy,
    created_at: template.createdAt?.toISOString() || now,
    updated_at: now,
  };
}

function cycleOverrideFromSupabase(row: any): CycleAttributeGovernanceOverride {
  return {
    id: row.id,
    cycleId: row.cycle_id,
    attributeKey: row.attribute_key,
    rangeDefinerRoles: row.range_definer_roles ?? undefined,
    rangeApproverRoles: row.range_approver_roles ?? undefined,
    valueProposerRoles: row.value_proposer_roles ?? undefined,
    valueApproverRoles: row.value_approver_roles ?? undefined,
    collectionMethod: row.collection_method ?? undefined,
    specificPersonId: row.specific_person_id ?? undefined,
    requiresEvidence: row.requires_evidence ?? undefined,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
}

function cycleOverrideToSupabase(o: Partial<CycleAttributeGovernanceOverride>): Record<string, unknown> {
  const now = new Date().toISOString();
  return {
    id: o.id,
    cycle_id: o.cycleId,
    attribute_key: o.attributeKey,
    range_definer_roles: o.rangeDefinerRoles ?? null,
    range_approver_roles: o.rangeApproverRoles ?? null,
    value_proposer_roles: o.valueProposerRoles ?? null,
    value_approver_roles: o.valueApproverRoles ?? null,
    collection_method: o.collectionMethod ?? null,
    specific_person_id: o.specificPersonId ?? null,
    requires_evidence: o.requiresEvidence ?? null,
    created_at: o.createdAt?.toISOString() ?? now,
    updated_at: now,
  };
}

function cycleConfigFromSupabase(row: any): CycleAttributeConfig {
  return {
    id: row.id,
    cycleId: row.cycle_id,
    trackId: row.track_id,
    attributeKey: row.attribute_key,
    rangeDefinerRoles: row.range_definer_roles,
    rangeApproverRoles: row.range_approver_roles,
    valueProposerRoles: row.value_proposer_roles,
    valueApproverRoles: row.value_approver_roles,
    collectionMethod: row.collection_method,
    specificPersonId: row.specific_person_id,
    requiresEvidence: row.requires_evidence,
    rangeStatus: row.range_status || 'pending',
    rangeValue: row.range_value,
    rangeProposedBy: row.range_proposed_by,
    rangeProposedAt: row.range_proposed_at ? new Date(row.range_proposed_at) : undefined,
    rangeAcceptedBy: row.range_accepted_by,
    rangeAcceptedAt: row.range_accepted_at ? new Date(row.range_accepted_at) : undefined,
    weight: row.weight == null ? null : Number(row.weight),
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
}

function cycleConfigToSupabase(config: Partial<CycleAttributeConfig>): Record<string, unknown> {
  const now = new Date().toISOString();
  return {
    id: config.id,
    cycle_id: config.cycleId,
    track_id: config.trackId,
    attribute_key: config.attributeKey,
    range_definer_roles: config.rangeDefinerRoles,
    range_approver_roles: config.rangeApproverRoles,
    value_proposer_roles: config.valueProposerRoles,
    value_approver_roles: config.valueApproverRoles,
    collection_method: config.collectionMethod,
    specific_person_id: config.specificPersonId,
    requires_evidence: config.requiresEvidence,
    range_status: config.rangeStatus,
    range_value: config.rangeValue,
    range_proposed_by: config.rangeProposedBy,
    range_proposed_at: config.rangeProposedAt?.toISOString(),
    range_accepted_by: config.rangeAcceptedBy,
    range_accepted_at: config.rangeAcceptedAt?.toISOString(),
    weight: config.weight,
    // Only an insert carries createdAt. An update leaves created_at alone: it orders
    // a track's criteria (its sessions and its versions read them in that order).
    created_at: config.createdAt?.toISOString(),
    updated_at: now,
  };
}

// ============================================================================
// Store State
// ============================================================================

interface AttributeGovernanceState {
  // Data
  templates: AttributeGovernanceTemplate[];
  cycleConfigs: CycleAttributeConfig[];
  /** Cycle-level governance overrides (no track_id). Loaded by fetchCycleGovernanceOverrides. */
  cycleOverrides: CycleAttributeGovernanceOverride[];
  
  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  fetchCycleConfigs: (cycleId: string) => Promise<void>;
  /**
   * Fetch cycle-level governance overrides for a cycle.
   * Merged in getEffectiveConfig between template and track.
   */
  fetchCycleGovernanceOverrides: (cycleId: string) => Promise<void>;
  /**
   * Fetch criteria configs for a specific track.
   * Per Track-Authoritative spec: Criteria are track-scoped.
   */
  /** Resolves true when the track's configs were read from the server, false when they
   *  could not be (no client, or the read failed). Callers that tell "none configured"
   *  apart from "not loaded" need this: an empty list alone cannot say which. */
  fetchTrackConfigs: (trackId: string) => Promise<boolean>;
  /**
   * Get criteria configs for a specific track.
   */
  getTrackConfigs: (trackId: string) => CycleAttributeConfig[];
  
  // Template CRUD
  createTemplate: (data: Omit<AttributeGovernanceTemplate, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateTemplate: (id: string, updates: Partial<AttributeGovernanceTemplate>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  
  // Cycle config CRUD. Update and delete resolve with the reason a write was not
  // saved (the server's, or the workflow's), or null when it was.
  createCycleConfig: (data: Omit<CycleAttributeConfig, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateCycleConfig: (id: string, updates: Partial<CycleAttributeConfig>) => Promise<{ error: string | null }>;
  deleteCycleConfig: (id: string) => Promise<{ error: string | null }>;
  // Cycle-level governance overrides (no track_id)
  upsertCycleOverride: (cycleId: string, attributeKey: string, data: Partial<Omit<CycleAttributeGovernanceOverride, 'id' | 'cycleId' | 'attributeKey' | 'createdAt' | 'updatedAt'>>) => Promise<void>;
  deleteCycleOverride: (cycleId: string, attributeKey: string) => Promise<void>;
  
  /**
   * Set one track criterion's value range (one Criteria & Scales row). The track's
   * Value Ranges tab and the cycle configure wizard both save a range through this:
   * accepted at once when the person may approve it, else proposed. A range that
   * differs from the one the cycle's workflow locks for the criterion is refused.
   */
  saveTrackRange: (
    configId: string,
    range: ValueRange,
    options: { by: string; accept: boolean; specificPersonId?: string },
  ) => Promise<{ error: string | null }>;

  /**
   * Set the weights of one track's criteria (migration 1047), by attribute key:
   * a weight on every criterion adding up to 100, or none (sessions on the track
   * then start from an even split). The track's Criteria tab and the cycle
   * configure wizard's Weights step both save through this. Refused before any
   * write when the set does not check (checkTrackWeights); the database refuses it
   * while the track's criteria are locked.
   */
  saveTrackCriteriaWeights: (trackId: string, weights: TrackWeights) => Promise<{ error: string | null }>;
  
  // Permission checks (capability-based; pass resolved userCapabilities from role → capabilities)
  canDefineRange: (attributeKey: string, userCapabilities: CapabilityId[], cycleId?: string, trackId?: string) => boolean;
  canApproveRange: (attributeKey: string, userCapabilities: CapabilityId[], cycleId?: string, trackId?: string) => boolean;
  canProposeValue: (attributeKey: string, userCapabilities: CapabilityId[], cycleId?: string, trackId?: string) => boolean;
  canApproveValue: (attributeKey: string, userCapabilities: CapabilityId[], cycleId?: string, trackId?: string) => boolean;
  
  // Getters
  getTemplateByKey: (attributeKey: string, scopeKind?: string, scopeId?: string) => AttributeGovernanceTemplate | undefined;
  getCycleOverride: (cycleId: string, attributeKey: string) => CycleAttributeGovernanceOverride | undefined;
  getCycleConfig: (cycleId: string, attributeKey: string, trackId?: string) => CycleAttributeConfig | undefined;
  getEffectiveConfig: (attributeKey: string, cycleId?: string, trackId?: string) => EffectiveAttributeConfig | undefined;
  isRangeAccepted: (cycleId: string, attributeKey: string, trackId?: string) => boolean;
}

// ============================================================================
// Default Template
// ============================================================================

const DEFAULT_TEMPLATE: Omit<AttributeGovernanceTemplate, 'id' | 'attributeKey' | 'createdAt' | 'updatedAt'> = {
  rangeDefinerRoles: ['workspace-admin', 'tenant-admin', 'system-admin'],
  rangeApproverRoles: ['workspace-admin', 'tenant-admin', 'system-admin'],
  valueProposerRoles: ['workspace-admin', 'tenant-admin', 'system-admin'],
  valueApproverRoles: ['workspace-admin', 'tenant-admin', 'system-admin'],
  collectionMethod: 'any_group_member',
  requiresEvidence: false,
  evidenceTypes: [],
  scopeKind: 'global',
  isActive: true,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useAttributeGovernanceStore = create<AttributeGovernanceState>()(
  (set, get) => ({
    templates: [],
    cycleConfigs: [],
    cycleOverrides: [],
    syncState: createInitialSyncState(),
      
      // ======================================================================
      // Supabase Sync
      // ======================================================================
      
      fetchFromSupabase: async () => {
        if (!isSupabaseAvailable()) {
          console.log('[AttributeGovernanceStore] Supabase not available');
          return;
        }
        
        set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
        
        try {
          const result = await fetchAll<any>(TEMPLATES_TABLE);
          
          if (result.error) {
            console.error('[AttributeGovernanceStore] Failed to fetch templates:', result.error);
            set({ syncState: { ...get().syncState, isLoading: false, syncError: result.error } });
            return;
          }
          
          const templates = (result.data || []).map(templateFromSupabase);
          
          set({
            templates,
            syncState: {
              isLoading: false,
              lastSyncedAt: new Date(),
              syncError: null,
            },
          });
          
          console.log(`[AttributeGovernanceStore] Loaded ${templates.length} templates from Supabase`);
        } catch (err) {
          console.error('[AttributeGovernanceStore] Fetch error:', err);
          set({ syncState: { ...get().syncState, isLoading: false, syncError: String(err) } });
        }
      },
      
      fetchCycleConfigs: async (cycleId: string) => {
        if (!isSupabaseAvailable()) {
          return;
        }
        
        try {
          // Get Supabase client and query directly with filter
          const { getSupabaseClient } = await import('../../lib/supabaseClient');
          const supabase = getSupabaseClient();
          
          const { data, error } = await supabase
            .from(CYCLE_CONFIGS_TABLE)
            .select('*')
            .eq('cycle_id', cycleId);
          
          if (error) {
            console.error('[AttributeGovernanceStore] Failed to fetch cycle configs:', error.message);
            return;
          }
          
          const newConfigs = (data || []).map(cycleConfigFromSupabase);
          
          // Merge with existing configs (replace configs for this cycle)
          set((state) => ({
            cycleConfigs: [
              ...state.cycleConfigs.filter(c => c.cycleId !== cycleId),
              ...newConfigs,
            ],
          }));
          
          console.log(`[AttributeGovernanceStore] Loaded ${newConfigs.length} cycle configs for cycle ${cycleId}`);
          await get().fetchCycleGovernanceOverrides(cycleId);
        } catch (err) {
          console.error('[AttributeGovernanceStore] Fetch cycle configs error:', err);
        }
      },

      fetchCycleGovernanceOverrides: async (cycleId: string) => {
        if (!isSupabaseAvailable()) return;
        try {
          const { getSupabaseClient } = await import('../../lib/supabaseClient');
          const supabase = getSupabaseClient();
          const { data, error } = await supabase
            .from(CYCLE_OVERRIDES_TABLE)
            .select('*')
            .eq('cycle_id', cycleId);
          if (error) {
            console.warn('[AttributeGovernanceStore] Failed to fetch cycle governance overrides:', error.message);
            return;
          }
          const newOverrides = (data || []).map((row: any) => cycleOverrideFromSupabase(row));
          set((state) => ({
            cycleOverrides: [
              ...state.cycleOverrides.filter(o => o.cycleId !== cycleId),
              ...newOverrides,
            ],
          }));
        } catch (err) {
          console.warn('[AttributeGovernanceStore] Fetch cycle governance overrides error:', err);
        }
      },

      fetchTrackConfigs: async (trackId: string) => {
        if (!isSupabaseAvailable()) return false;
        const supabase = getSafeSupabaseClient();
        if (!supabase) return false;
        const { data: rows, error } = await supabase
          .from(CYCLE_CONFIGS_TABLE)
          .select('*')
          .eq('track_id', trackId);
        if (error) {
          console.error('[AttributeGovernanceStore] Failed to fetch track configs:', error.message);
          return false;
        }
        const newConfigs = (rows || []).map((row: any) => cycleConfigFromSupabase(row));
        set((state) => ({
          cycleConfigs: [
            ...state.cycleConfigs.filter(c => c.trackId !== trackId),
            ...newConfigs,
          ],
        }));
        return true;
      },

      /** S3: Get criteria set (attribute configs) for a track. Range schema is per-config (rangeValue, rangeStatus). */
      getTrackConfigs: (trackId: string) => {
        return get().cycleConfigs.filter(c => c.trackId === trackId);
      },
      
      // ======================================================================
      // Template CRUD
      // ======================================================================
      
      createTemplate: async (data) => {
        // Authorization check is non-blocking - log only for audit purposes
        const resource = buildResourceContext('attribute', data.attributeKey, { createdBy: data.createdBy });
        const decision = checkAuthorization('attributes.define', resource);
        if (!decision.allowed) {
          console.log('[AttributeGovernanceStore] Authorization note for attributes.define (createTemplate):', decision.reasons[0]?.code || 'NO_POLICY');
        }
        const id = generateUUID();
        const now = new Date();
        
        const template: AttributeGovernanceTemplate = {
          ...data,
          id,
          createdAt: now,
          updatedAt: now,
        };
        
        // Optimistic update
        set((state) => ({
          templates: [...state.templates, template],
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await insertRecord(TEMPLATES_TABLE, templateToSupabase(template));
          if (result.error) {
            console.error('[AttributeGovernanceStore] Failed to insert template:', result.error);
            // Rollback
            set((state) => ({
              templates: state.templates.filter(t => t.id !== id),
            }));
            throw new Error(result.error);
          }
        }
        
        return id;
      },
      
      updateTemplate: async (id, updates) => {
        const existing = get().templates.find(t => t.id === id);
        if (!existing) return;
        // Authorization check is non-blocking - log only for audit purposes
        const resource = buildResourceContext('attribute', existing.attributeKey, { createdBy: existing.createdBy });
        const decision = checkAuthorization('attributes.define', resource);
        if (!decision.allowed) {
          console.log('[AttributeGovernanceStore] Authorization note for attributes.define (updateTemplate):', decision.reasons[0]?.code || 'NO_POLICY');
        }
        const updated = { ...existing, ...updates, updatedAt: new Date() };
        
        // Optimistic update
        set((state) => ({
          templates: state.templates.map(t => t.id === id ? updated : t),
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await updateRecord(TEMPLATES_TABLE, id, templateToSupabase(updates));
          if (result.error) {
            console.error('[AttributeGovernanceStore] Failed to update template:', result.error);
            // Rollback
            set((state) => ({
              templates: state.templates.map(t => t.id === id ? existing : t),
            }));
          }
        }
      },
      
      deleteTemplate: async (id) => {
        const existing = get().templates.find(t => t.id === id);
        if (!existing) return;
        // Authorization check is non-blocking - log only for audit purposes
        const resource = buildResourceContext('attribute', existing.attributeKey, { createdBy: existing.createdBy });
        const decision = checkAuthorization('attributes.define', resource);
        if (!decision.allowed) {
          console.log('[AttributeGovernanceStore] Authorization note for attributes.define (deleteTemplate):', decision.reasons[0]?.code || 'NO_POLICY');
        }
        // Optimistic update
        set((state) => ({
          templates: state.templates.filter(t => t.id !== id),
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await deleteRecord(TEMPLATES_TABLE, id);
          if (result.error) {
            console.error('[AttributeGovernanceStore] Failed to delete template:', result.error);
            // Rollback
            set((state) => ({
              templates: [...state.templates, existing],
            }));
          }
        }
      },
      
      // ======================================================================
      // Cycle Config CRUD
      // ======================================================================
      
      createCycleConfig: async (data) => {
        // Workflow governance: if a canonical locked range exists for this attribute,
        // auto-apply it so the seeded config is always the workflow-canonical value.
        // This ensures even template-seeded configs cannot accidentally diverge.
        let effectiveData = { ...data };
        if (data.cycleId) {
          const governance = await resolveWorkflowCriteriaGovernance(data.cycleId);
          if (governance) {
            const lockedRange = getLockedRange(governance, data.attributeKey);
            if (lockedRange) {
              effectiveData = {
                ...effectiveData,
                rangeValue: lockedRange as ValueRange,
                rangeStatus: 'accepted',
                rangeAcceptedBy: effectiveData.rangeAcceptedBy ?? 'workflow',
                rangeAcceptedAt: effectiveData.rangeAcceptedAt ?? new Date(),
              };
            }
          }
        }

        // Authorization check is non-blocking - log only for audit purposes
        const resource = buildResourceContext('attribute', data.attributeKey, {});
        const decision = checkAuthorization('attributes.define', resource);
        if (!decision.allowed) {
          console.log('[AttributeGovernanceStore] Authorization note for attributes.define (createCycleConfig):', decision.reasons[0]?.code || 'NO_POLICY');
        }
        const id = generateUUID();
        const now = new Date();
        
        const config: CycleAttributeConfig = {
          ...effectiveData,
          id,
          rangeStatus: effectiveData.rangeStatus || 'pending',
          createdAt: now,
          updatedAt: now,
        };
        
        // Optimistic update
        set((state) => ({
          cycleConfigs: [...state.cycleConfigs, config],
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await insertRecord(CYCLE_CONFIGS_TABLE, cycleConfigToSupabase(config));
          if (result.error) {
            console.error('[AttributeGovernanceStore] Failed to insert cycle config:', result.error);
            // Rollback
            set((state) => ({
              cycleConfigs: state.cycleConfigs.filter(c => c.id !== id),
            }));
            throw new Error(result.error);
          }
        }
        
        return id;
      },
      
      updateCycleConfig: async (id, updates) => {
        const existing = get().cycleConfigs.find(c => c.id === id);
        if (!existing) return { error: 'This criterion is not loaded.' };
        // Authorization check is non-blocking - log only for audit purposes
        const resource = buildResourceContext('attribute', existing.attributeKey, {});
        const decision = checkAuthorization('attributes.define', resource);
        if (!decision.allowed) {
          console.log('[AttributeGovernanceStore] Authorization note for attributes.define (updateCycleConfig):', decision.reasons[0]?.code || 'NO_POLICY');
        }
        const updated = { ...existing, ...updates, updatedAt: new Date() };
        
        // Optimistic update
        set((state) => ({
          cycleConfigs: state.cycleConfigs.map(c => c.id === id ? updated : c),
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await updateRecord(CYCLE_CONFIGS_TABLE, id, cycleConfigToSupabase(updates));
          if (result.error) {
            console.error('[AttributeGovernanceStore] Failed to update cycle config:', result.error);
            // Rollback
            set((state) => ({
              cycleConfigs: state.cycleConfigs.map(c => c.id === id ? existing : c),
            }));
            return { error: result.error };
          }
        }
        return { error: null };
      },

      deleteCycleConfig: async (id) => {
        const existing = get().cycleConfigs.find(c => c.id === id);
        if (!existing) return { error: 'This criterion is not loaded.' };

        // Workflow governance: block deletion of required locked attributes
        if (existing.cycleId) {
          const governance = await resolveWorkflowCriteriaGovernance(existing.cycleId);
          if (governance) {
            const violation = checkCanRemoveAttribute(governance, existing.attributeKey);
            if (violation) {
              console.warn('[AttributeGovernanceStore] Workflow governance blocked deleteCycleConfig:', violation.message);
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('authorization-denied', {
                  detail: { store: 'attributeGovernance', action: 'deleteCycleConfig', message: violation.message, code: violation.code },
                }));
              }
              return { error: violation.message };
            }
          }
        }

        // Authorization check is non-blocking - log only for audit purposes
        const resource = buildResourceContext('attribute', existing.attributeKey, {});
        const decision = checkAuthorization('attributes.define', resource);
        if (!decision.allowed) {
          console.log('[AttributeGovernanceStore] Authorization note for attributes.define (deleteCycleConfig):', decision.reasons[0]?.code || 'NO_POLICY');
        }
        // Optimistic update
        set((state) => ({
          cycleConfigs: state.cycleConfigs.filter(c => c.id !== id),
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const result = await deleteRecord(CYCLE_CONFIGS_TABLE, id);
          if (result.error) {
            console.error('[AttributeGovernanceStore] Failed to delete cycle config:', result.error);
            // Rollback
            set((state) => ({
              cycleConfigs: [...state.cycleConfigs, existing],
            }));
            return { error: result.error };
          }
        }
        return { error: null };
      },

      upsertCycleOverride: async (cycleId, attributeKey, data) => {
        if (!isSupabaseAvailable()) return;
        const existing = get().cycleOverrides.find(o => o.cycleId === cycleId && o.attributeKey === attributeKey);
        const now = new Date();
        if (existing) {
          const updated: CycleAttributeGovernanceOverride = {
            ...existing,
            ...data,
            updatedAt: now,
          };
          set((state) => ({
            cycleOverrides: state.cycleOverrides.map(o =>
              o.cycleId === cycleId && o.attributeKey === attributeKey ? updated : o
            ),
          }));
          const result = await updateRecord(CYCLE_OVERRIDES_TABLE, existing.id, cycleOverrideToSupabase(updated));
          if (result.error) {
            set((state) => ({
              cycleOverrides: state.cycleOverrides.map(o =>
                o.cycleId === cycleId && o.attributeKey === attributeKey ? existing : o
              ),
            }));
            throw new Error(result.error);
          }
        } else {
          const id = generateUUID();
          const row: CycleAttributeGovernanceOverride = {
            id,
            cycleId,
            attributeKey,
            rangeDefinerRoles: data.rangeDefinerRoles,
            rangeApproverRoles: data.rangeApproverRoles,
            valueProposerRoles: data.valueProposerRoles,
            valueApproverRoles: data.valueApproverRoles,
            collectionMethod: data.collectionMethod,
            specificPersonId: data.specificPersonId,
            requiresEvidence: data.requiresEvidence,
            createdAt: now,
            updatedAt: now,
          };
          set((state) => ({
            cycleOverrides: [...state.cycleOverrides.filter(o => !(o.cycleId === cycleId && o.attributeKey === attributeKey)), row],
          }));
          const result = await insertRecord(CYCLE_OVERRIDES_TABLE, cycleOverrideToSupabase(row));
          if (result.error) {
            set((state) => ({
              cycleOverrides: state.cycleOverrides.filter(o => !(o.cycleId === cycleId && o.attributeKey === attributeKey)),
            }));
            throw new Error(result.error);
          }
        }
      },

      deleteCycleOverride: async (cycleId, attributeKey) => {
        const existing = get().cycleOverrides.find(o => o.cycleId === cycleId && o.attributeKey === attributeKey);
        if (!existing) return;
        set((state) => ({
          cycleOverrides: state.cycleOverrides.filter(o => !(o.cycleId === cycleId && o.attributeKey === attributeKey)),
        }));
        if (isSupabaseAvailable()) {
          const result = await deleteRecord(CYCLE_OVERRIDES_TABLE, existing.id);
          if (result.error) {
            set((state) => ({
              cycleOverrides: [...state.cycleOverrides, existing],
            }));
            throw new Error(result.error);
          }
        }
      },
      
      // ======================================================================
      // Range Workflow
      // ======================================================================
      
      // A range is set on one track's row, never "the cycle's": the row is chosen by id.
      // (proposeRange/acceptRange/rejectRange took a cycle and an attribute key, so they
      // created a row with no track — refused, track_id is NOT NULL — or overwrote
      // whichever track's row they found first. Removed 2026-11-02.)
      saveTrackRange: async (configId, range, { by, accept, specificPersonId }) => {
        const config = get().cycleConfigs.find(c => c.id === configId);
        if (!config) return { error: 'This criterion is not loaded.' };

        // Workflow governance: a range that diverges from the workflow's canonical one is refused.
        if (config.cycleId) {
          const governance = await resolveWorkflowCriteriaGovernance(config.cycleId);
          if (governance) {
            const violation = checkCanProposeRange(governance, config.attributeKey, range as unknown as Record<string, unknown>);
            if (violation) {
              console.warn('[AttributeGovernanceStore] Workflow governance blocked saveTrackRange:', violation.message);
              return { error: violation.message };
            }
          }
        }

        const now = new Date();
        return get().updateCycleConfig(configId, {
          rangeValue: range,
          rangeStatus: accept ? 'accepted' : 'proposed',
          rangeProposedBy: by,
          rangeProposedAt: now,
          rangeAcceptedBy: accept ? by : undefined,
          rangeAcceptedAt: accept ? now : undefined,
          ...(specificPersonId !== undefined ? { specificPersonId } : {}),
        });
      },

      saveTrackCriteriaWeights: async (trackId, weights) => {
        const rows = get().cycleConfigs.filter(c => c.trackId === trackId);
        if (rows.length === 0) return { error: 'This track has no criteria loaded.' };
        // Every criterion on the track is decided: a key not given has no weight.
        const full: TrackWeights = Object.fromEntries(rows.map(r => [r.attributeKey, weights[r.attributeKey] ?? null]));
        const check = checkTrackWeights(full);
        if (!check.ok) return { error: check.error ?? 'These weights cannot be saved.' };
        for (const row of rows) {
          const next = full[row.attributeKey];
          if ((row.weight ?? null) === next) continue;
          const { error } = await get().updateCycleConfig(row.id, { weight: next });
          if (error) return { error };
        }
        return { error: null };
      },
      
      // ======================================================================
      // Permission Checks (using three-layer authorization model)
      // ======================================================================
      
      canDefineRange: (attributeKey, userCapabilities, cycleId, trackId) => {
        const effective = get().getEffectiveConfig(attributeKey, cycleId, trackId);
        if (!effective) return userCapabilities.includes('VALUE_RANGE_DEFINE') || userCapabilities.includes('SCALES_DEFINE');
        
        const config: AttributeGovernanceConfig = {
          attributeKey,
          cycleId,
          rangeDefinerRoles: effective.rangeDefinerRoles,
          rangeApproverRoles: effective.rangeApproverRoles,
          valueProposerRoles: effective.valueProposerRoles,
          valueApproverRoles: effective.valueApproverRoles,
          rangeStatus: effective.rangeStatus,
        };
        
        return checkCanDefineRange(userCapabilities, config);
      },
      
      canApproveRange: (attributeKey, userCapabilities, cycleId, trackId) => {
        const effective = get().getEffectiveConfig(attributeKey, cycleId, trackId);
        if (!effective) return userCapabilities.includes('VALUE_RANGE_APPROVE');
        
        const config: AttributeGovernanceConfig = {
          attributeKey,
          cycleId,
          rangeDefinerRoles: effective.rangeDefinerRoles,
          rangeApproverRoles: effective.rangeApproverRoles,
          valueProposerRoles: effective.valueProposerRoles,
          valueApproverRoles: effective.valueApproverRoles,
          rangeStatus: effective.rangeStatus,
        };
        
        return checkCanApproveRange(userCapabilities, config);
      },
      
      canProposeValue: (attributeKey, userCapabilities, cycleId, trackId) => {
        const effective = get().getEffectiveConfig(attributeKey, cycleId, trackId);
        if (!effective) return userCapabilities.includes('VALUE_ENTER');

        const config: AttributeGovernanceConfig = {
          attributeKey,
          cycleId,
          rangeDefinerRoles: effective.rangeDefinerRoles,
          rangeApproverRoles: effective.rangeApproverRoles,
          valueProposerRoles: effective.valueProposerRoles,
          valueApproverRoles: effective.valueApproverRoles,
          rangeStatus: effective.rangeStatus,
        };

        // Range-acceptance is a cycle-scoped gate: only enforce it when operating
        // within a specific cycle. Outside a cycle (e.g. item detail Values tab),
        // skip the range check and only require VALUE_ENTER capability.
        const requireRangeAccepted = !!cycleId;
        return checkCanProposeValue(userCapabilities, config, requireRangeAccepted);
      },
      
      canApproveValue: (attributeKey, userCapabilities, cycleId, trackId) => {
        const effective = get().getEffectiveConfig(attributeKey, cycleId, trackId);
        if (!effective) return userCapabilities.includes('VALUE_ACCEPT');
        
        const config: AttributeGovernanceConfig = {
          attributeKey,
          cycleId,
          rangeDefinerRoles: effective.rangeDefinerRoles,
          rangeApproverRoles: effective.rangeApproverRoles,
          valueProposerRoles: effective.valueProposerRoles,
          valueApproverRoles: effective.valueApproverRoles,
          rangeStatus: effective.rangeStatus,
        };
        
        return checkCanApproveValue(userCapabilities, config);
      },
      
      // ======================================================================
      // Getters
      // ======================================================================
      
      getTemplateByKey: (attributeKey, scopeKind = 'global', scopeId) => {
        return get().templates.find(t => 
          t.attributeKey === attributeKey && 
          t.scopeKind === scopeKind &&
          t.scopeId === scopeId &&
          t.isActive
        );
      },
      
      getCycleOverride: (cycleId, attributeKey) => {
        return get().cycleOverrides.find(
          o => o.cycleId === cycleId && o.attributeKey === attributeKey
        );
      },

      getCycleConfig: (cycleId, attributeKey, trackId) => {
        const configs = get().cycleConfigs.filter(
          c => c.cycleId === cycleId && c.attributeKey === attributeKey
        );
        if (trackId) {
          const forTrack = configs.find(c => c.trackId === trackId);
          if (forTrack) return forTrack;
        }
        return configs[0];
      },
      
      /**
       * Resolve effective governance: GLOBAL (template) → CYCLE (override) → TRACK (cycle_attribute_configs).
       * First non-null wins per dimension.
       */
      getEffectiveConfig: (attributeKey, cycleId, trackId) => {
        const template = get().getTemplateByKey(attributeKey);
        const cycleOverride = cycleId ? get().getCycleOverride(cycleId, attributeKey) : undefined;
        const trackConfig = cycleId ? get().getCycleConfig(cycleId, attributeKey, trackId) : undefined;
        
        if (!template && !cycleOverride && !trackConfig) {
          return {
            attributeKey,
            cycleId,
            ...DEFAULT_TEMPLATE,
            isFromTemplate: false,
          };
        }
        
        return {
          attributeKey,
          cycleId,
          rangeDefinerRoles: trackConfig?.rangeDefinerRoles ?? cycleOverride?.rangeDefinerRoles ?? (template?.rangeDefinerRoles ?? DEFAULT_TEMPLATE.rangeDefinerRoles),
          rangeApproverRoles: trackConfig?.rangeApproverRoles ?? cycleOverride?.rangeApproverRoles ?? (template?.rangeApproverRoles ?? DEFAULT_TEMPLATE.rangeApproverRoles),
          valueProposerRoles: trackConfig?.valueProposerRoles ?? cycleOverride?.valueProposerRoles ?? (template?.valueProposerRoles ?? DEFAULT_TEMPLATE.valueProposerRoles),
          valueApproverRoles: trackConfig?.valueApproverRoles ?? cycleOverride?.valueApproverRoles ?? (template?.valueApproverRoles ?? DEFAULT_TEMPLATE.valueApproverRoles),
          collectionMethod: trackConfig?.collectionMethod ?? cycleOverride?.collectionMethod ?? (template?.collectionMethod ?? DEFAULT_TEMPLATE.collectionMethod),
          specificPersonId: trackConfig?.specificPersonId ?? cycleOverride?.specificPersonId ?? template?.specificPersonId,
          requiresEvidence: trackConfig?.requiresEvidence ?? cycleOverride?.requiresEvidence ?? (template?.requiresEvidence ?? DEFAULT_TEMPLATE.requiresEvidence),
          evidenceTypes: template?.evidenceTypes || DEFAULT_TEMPLATE.evidenceTypes,
          rangeStatus: trackConfig?.rangeStatus,
          rangeValue: trackConfig?.rangeValue,
          isFromTemplate: !!template,
          templateId: template?.id,
          cycleConfigId: trackConfig?.id,
        };
      },
      
      isRangeAccepted: (cycleId, attributeKey, trackId) => {
      const config = get().getCycleConfig(cycleId, attributeKey, trackId);
      return config?.rangeStatus === 'accepted';
    },
  })
);

// Initial fetch is driven by useSupabaseSync (App.tsx) to avoid duplicate requests and AbortErrors.

// ============================================================================
// Selector Hooks
// ============================================================================

export function useTemplates() {
  return useAttributeGovernanceStore((state) => state.templates);
}

export function useCycleConfigs(cycleId: string) {
  return useAttributeGovernanceStore((state) => 
    state.cycleConfigs.filter(c => c.cycleId === cycleId)
  );
}

export function useEffectiveConfig(attributeKey: string, cycleId?: string, trackId?: string) {
  return useAttributeGovernanceStore((state) => 
    state.getEffectiveConfig(attributeKey, cycleId, trackId)
  );
}
